import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { authManager } from '../utils/auth';

const Base_Url = process.env.REACT_APP_API_BASE_URL;

function QuizLeads() {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({});
    const [stats, setStats] = useState(null);
    const [selectedLead, setSelectedLead] = useState(null);
    const [filters, setFilters] = useState({
        interestSegment: '',
        status: '',
        campaign: '',
        startDate: '',
        endDate: '',
    });

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        fetchStats();
        fetchLeads();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchStats = async () => {
        try {
            const res = await authManager.authenticatedFetch(
                `${Base_Url}/admin/quiz/leads/stats`
            );
            const data = await res.json();
            if (data.status === 'success') {
                setStats(data.data);
            }
        } catch (error) {
            console.error('Error fetching quiz lead stats:', error);
        }
    };

    const fetchLeads = async (page = 1) => {
        try {
            setLoading(true);
            const queryParams = new URLSearchParams({
                page,
                limit: 50,
                ...(filters.interestSegment && { interestSegment: filters.interestSegment }),
                ...(filters.status && { status: filters.status }),
                ...(filters.campaign && { campaign: filters.campaign }),
                ...(filters.startDate && { startDate: filters.startDate }),
                ...(filters.endDate && { endDate: filters.endDate }),
            });

            const res = await authManager.authenticatedFetch(
                `${Base_Url}/admin/quiz/leads/list?${queryParams}`
            );
            const data = await res.json();

            if (data.status === 'success') {
                setLeads(data.data.leads || []);
                setPagination(data.data.pagination || {});
            } else {
                toast.error(data.message || 'Failed to fetch quiz leads');
            }
        } catch (error) {
            console.error('Error fetching quiz leads:', error);
            toast.error('Error fetching quiz leads');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleSearch = (e) => {
        e.preventDefault();
        fetchLeads(1);
    };

    const handleClearFilters = () => {
        setFilters({
            interestSegment: '', status: '', campaign: '', startDate: '', endDate: '',
        });
        setTimeout(() => fetchLeads(1), 0);
    };

    const renderStatCard = (title, value, iconEl) => (
        <div className="col-12 col-md-6 col-lg-3 mb-4" key={title}>
            <div className="card h-100">
                <div className="card-body d-flex text-center align-items-center mt-3 mb-3">
                    <div className="col-8 col-md-8 col-lg-8">
                        <h5 className="card-title w-100 pb-2">{title}</h5>
                        <h2 className="f_black">{value ?? 0}</h2>
                    </div>
                    <div className="col-4 col-md-4 col-lg-4">
                        {iconEl}
                    </div>
                </div>
            </div>
        </div>
    );

    const topGroup = (rows) => {
        if (!rows || rows.length === 0) return '—';
        const sorted = [...rows].sort((a, b) => b.count - a.count);
        return `${sorted[0]._id || 'unknown'} (${sorted[0].count})`;
    };

    return (
        <>
            <ToastContainer autoClose={3000} />
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-6">
                            <h1 className="m-0 txt_lft">Quiz Leads</h1>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container-fluid">
                    {/* Stats */}
                    <div className="row justify-content-center">
                        {renderStatCard('Total Leads', stats?.total, <i className="fa-solid fa-users text-primary fa-3x" />)}
                        {renderStatCard('Top Segment', topGroup(stats?.bySegment), <i className="fa-solid fa-bullseye text-warning fa-3x" />)}
                        {renderStatCard('Top Campaign', topGroup(stats?.byCampaign), <i className="fa-solid fa-bullhorn text-info fa-3x" />)}
                        {renderStatCard('Converted', stats?.byStatus?.find((s) => s._id === 'converted')?.count, <i className="fa-solid fa-circle-check text-success fa-3x" />)}
                    </div>

                    {/* Filters */}
                    <div className="card mb-3">
                        <div className="card-body">
                            <form onSubmit={handleSearch} className="row align-items-end">
                                <div className="col-md-2">
                                    <label>Segment</label>
                                    <select
                                        className="form-control"
                                        name="interestSegment"
                                        value={filters.interestSegment}
                                        onChange={handleFilterChange}
                                    >
                                        <option value="">All</option>
                                        <option value="mental_health">Mental health</option>
                                        <option value="financial">Financial</option>
                                        <option value="family">Family</option>
                                        <option value="all">All of the above</option>
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <label>Status</label>
                                    <select
                                        className="form-control"
                                        name="status"
                                        value={filters.status}
                                        onChange={handleFilterChange}
                                    >
                                        <option value="">All</option>
                                        <option value="new">New</option>
                                        <option value="nurtured">Nurtured</option>
                                        <option value="converted">Converted</option>
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label>Campaign</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="campaign"
                                        value={filters.campaign}
                                        onChange={handleFilterChange}
                                        placeholder="utm_campaign"
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label>From</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        name="startDate"
                                        value={filters.startDate}
                                        onChange={handleFilterChange}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label>To</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        name="endDate"
                                        value={filters.endDate}
                                        onChange={handleFilterChange}
                                    />
                                </div>
                                <div className="col-md-12 mt-2">
                                    <button type="submit" className="btn btn-primary mr-2">
                                        Filter
                                    </button>
                                    <button type="button" className="btn btn-secondary" onClick={handleClearFilters}>
                                        Clear
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="table-responsive">
                                {loading ? (
                                    <div className="text-center p-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="sr-only">Loading...</span>
                                        </div>
                                    </div>
                                ) : leads.length > 0 ? (
                                    <>
                                        <table width="100%" className="table table-striped table-bordered txt_lft">
                                            <thead>
                                                <tr>
                                                    <th>Email</th>
                                                    <th style={{ width: '140px' }}>Segment</th>
                                                    <th style={{ width: '110px' }}>Status</th>
                                                    <th style={{ width: '160px' }}>Campaign</th>
                                                    <th style={{ width: '160px' }}>Submitted</th>
                                                    <th style={{ width: '80px' }}>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {leads.map((lead) => (
                                                    <tr key={lead._id}>
                                                        <td>{lead.email}</td>
                                                        <td>{lead.interestSegment}</td>
                                                        <td>{lead.status}</td>
                                                        <td>{lead.acquisition_utm_campaign || '—'}</td>
                                                        <td>{lead.createdAt ? new Date(lead.createdAt).toLocaleString() : '—'}</td>
                                                        <td align="center">
                                                            <button
                                                                className="crsr_pntr btn btn-link p-0"
                                                                onClick={() => setSelectedLead(lead)}
                                                                data-toggle="modal"
                                                                data-target="#modalViewLead"
                                                                title="View details"
                                                            >
                                                                <i className="fa-solid fa-eye f_blue" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>

                                        {/* Pagination */}
                                        {pagination.pages > 1 && (
                                            <nav className="mt-3">
                                                <ul className="pagination justify-content-center">
                                                    <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                                        <button
                                                            className="page-link"
                                                            onClick={() => fetchLeads(pagination.page - 1)}
                                                            disabled={pagination.page === 1}
                                                        >
                                                            Previous
                                                        </button>
                                                    </li>
                                                    {[...Array(pagination.pages)].map((_, i) => (
                                                        <li key={i} className={`page-item ${pagination.page === i + 1 ? 'active' : ''}`}>
                                                            <button
                                                                className="page-link"
                                                                onClick={() => fetchLeads(i + 1)}
                                                            >
                                                                {i + 1}
                                                            </button>
                                                        </li>
                                                    ))}
                                                    <li className={`page-item ${pagination.page === pagination.pages ? 'disabled' : ''}`}>
                                                        <button
                                                            className="page-link"
                                                            onClick={() => fetchLeads(pagination.page + 1)}
                                                            disabled={pagination.page === pagination.pages}
                                                        >
                                                            Next
                                                        </button>
                                                    </li>
                                                </ul>
                                            </nav>
                                        )}
                                    </>
                                ) : (
                                    <div className="text-center p-5">
                                        <p>No quiz leads found</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* View Details Modal */}
            <div className="modal fade" id="modalViewLead" tabIndex="-1">
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Lead Details</h5>
                            <button type="button" className="close" data-dismiss="modal">
                                <span>&times;</span>
                            </button>
                        </div>
                        <div className="modal-body">
                            {selectedLead && (
                                <>
                                    <table className="table table-borderless table-sm txt_lft">
                                        <tbody>
                                            <tr>
                                                <th style={{ width: '160px' }}>Email</th>
                                                <td>{selectedLead.email}</td>
                                            </tr>
                                            <tr>
                                                <th>Segment</th>
                                                <td>{selectedLead.interestSegment}</td>
                                            </tr>
                                            <tr>
                                                <th>Status</th>
                                                <td>{selectedLead.status}</td>
                                            </tr>
                                            <tr>
                                                <th>Converted User</th>
                                                <td>
                                                    {selectedLead.convertedUserId ? (
                                                        <Link to={`/users/${selectedLead.convertedUserId}`}>
                                                            {selectedLead.convertedUserId}
                                                        </Link>
                                                    ) : '—'}
                                                </td>
                                            </tr>
                                            <tr>
                                                <th>Submitted</th>
                                                <td>{selectedLead.createdAt ? new Date(selectedLead.createdAt).toLocaleString() : '—'}</td>
                                            </tr>
                                            <tr>
                                                <th>UTM Source</th>
                                                <td>{selectedLead.acquisition_utm_source || '—'}</td>
                                            </tr>
                                            <tr>
                                                <th>UTM Medium</th>
                                                <td>{selectedLead.acquisition_utm_medium || '—'}</td>
                                            </tr>
                                            <tr>
                                                <th>UTM Campaign</th>
                                                <td>{selectedLead.acquisition_utm_campaign || '—'}</td>
                                            </tr>
                                            <tr>
                                                <th>UTM Content</th>
                                                <td>{selectedLead.acquisition_utm_content || '—'}</td>
                                            </tr>
                                            <tr>
                                                <th>UTM Term</th>
                                                <td>{selectedLead.acquisition_utm_term || '—'}</td>
                                            </tr>
                                            <tr>
                                                <th>User Agent</th>
                                                <td className="text-break">{selectedLead.metadata?.userAgent || '—'}</td>
                                            </tr>
                                            <tr>
                                                <th>IP Address</th>
                                                <td>{selectedLead.metadata?.ipAddress || '—'}</td>
                                            </tr>
                                        </tbody>
                                    </table>

                                    <h6 className="mt-3">Quiz Answers</h6>
                                    <table className="table table-striped table-bordered table-sm txt_lft">
                                        <thead>
                                            <tr>
                                                <th>Question</th>
                                                <th>Answer</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(selectedLead.answers || []).map((answer, i) => (
                                                <tr key={i}>
                                                    <td>{answer.questionText || answer.questionKey}</td>
                                                    <td>{Array.isArray(answer.value) ? answer.value.join(', ') : String(answer.value)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>

                                    {selectedLead.reportShown && (
                                        <>
                                            <h6 className="mt-3">Report Shown</h6>
                                            <table className="table table-borderless table-sm txt_lft">
                                                <tbody>
                                                    <tr>
                                                        <th style={{ width: '160px' }}>Nurture Track</th>
                                                        <td>{selectedLead.reportShown.nurtureTrack || '—'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Headline</th>
                                                        <td>{selectedLead.reportShown.headline || '—'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Gap Text</th>
                                                        <td>{selectedLead.reportShown.gapText || '—'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Premium Value Text</th>
                                                        <td>{selectedLead.reportShown.premiumValueText || '—'}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" data-dismiss="modal">
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default QuizLeads
